import { GlobalBookingTravelClass, TripType } from '@fcom/core';
import { FinnairMultiCitySearchInterface, GlobalBookingTripDates, HistogramBar } from '@fcom/common';
import { AvailabilityEntry, LowestPriceOfPeriod, Price } from '@fcom/dapi/api/models';
import { DateFormat, LocalDate, TzDate, isNotBlank, isNull, quantityOfMonths, rangeFrom } from '@fcom/utils';
import { Pattern } from '@fcom/utils/date.format';
import { Amount } from '@fcom/dapi';

import {
  CalendarPrices,
  DatePickerPrices,
  FlightSearchParams,
  PreviousSearch,
  SeasonalNotificationData,
} from '../interfaces';

export const createDeeplinkPathFromFlightSearchParams = ({
  locale,
  locations,
  paxAmount,
  travelClass,
  travelDates,
  tripType,
  promoCode,
  isAward,
}: FlightSearchParams): string => {
  const { adults, c15s, children, infants } = paxAmount;

  const flights = [
    {
      origin: locations[0].origin.locationCode,
      destination: locations[0].destination.locationCode,
      departureDate: travelDates?.departureDate?.toString(),
    },
  ];

  if (tripType === TripType.RETURN) {
    flights.push({
      origin: locations[0].destination.locationCode,
      destination: locations[0].origin.locationCode,
      departureDate: travelDates?.returnDate?.toString(),
    });
  }

  if (tripType === TripType.MULTICITY) {
    flights.push({
      origin: locations[1].origin.locationCode,
      destination: locations[1].destination.locationCode,
      departureDate: travelDates?.returnDate?.toString(),
    });
  }

  const urlParams: FinnairMultiCitySearchInterface = {
    flights,
    cabin: travelClass.toUpperCase() as GlobalBookingTravelClass,
    adults,
    c15s,
    children,
    infants,
    isAward: isAward || undefined,
    promoCode: isAward ? undefined : promoCode,
  };

  Object.keys(urlParams).forEach((key) => {
    if (urlParams[key] === (undefined || null)) {
      delete urlParams[key];
    }
  });

  const params = encodeURIComponent(JSON.stringify(urlParams));
  return `/${locale}/booking/flight-selection?json=${params}`;
};

export const mapInstantSearchPricesToCalendarPrices = (priceResponse: LowestPriceOfPeriod): CalendarPrices => {
  const lowestYearlyPrice =
    priceResponse?.prices?.length > 0
      ? Math.min(...priceResponse.prices.map(({ price }: Price) => price).filter((p) => !isNull(p)))
      : Infinity;

  return (
    priceResponse?.prices?.reduce(
      (allPrices: CalendarPrices, { date, price, noFlight }: Price) => ({
        ...allPrices,
        [date]: price
          ? {
              amount: {
                amount: price ? String(price) : null,
                currencyCode: priceResponse?.currency,
              },
              isLowestPrice: price === lowestYearlyPrice,
              noFlight,
            }
          : undefined,
      }),
      {}
    ) ?? {}
  );
};

export const createHistogramData = (
  priceResponse: LowestPriceOfPeriod,
  dates: string[],
  dateTranslations: Record<string, string | string[]>
): HistogramBar[] => {
  let histogramBarData = undefined;
  if (dates.length === 0) {
    return histogramBarData;
  }

  const lowestPriceForMonth = priceResponse?.lowestPriceForMonth;
  const now = LocalDate.now();

  histogramBarData = rangeFrom(
    0,
    quantityOfMonths(new LocalDate(dates[0]), new LocalDate(dates[dates.length - 1]))
  ).map((_, currentIndex) => {
    const current = now.firstDayOfMonth.plusMonths(currentIndex);
    const date = current.id.substring(0, 7);

    return {
      amount: date in lowestPriceForMonth ? String(lowestPriceForMonth[date]?.price) : '0',
      currencyCode: priceResponse?.currency,
      text: new DateFormat(dateTranslations).format(current, DateFormat.MONTH_SHORT),
      noFlight: date in lowestPriceForMonth ? lowestPriceForMonth[date]?.noFlight : true,
      index: currentIndex,
      ...(now.lt(current) && current.localMonth === 1 && { spacer: { value: String(current.localYear) } }),
    };
  });

  return histogramBarData;
};

export const seasonalNotificationHandler = (
  availabilityEntry: AvailabilityEntry[],
  dateTranslations: Record<string, string | string[]>
): SeasonalNotificationData => {
  return availabilityEntry.reduce((acc: SeasonalNotificationData, availability: AvailabilityEntry, index: number) => {
    const postFix: string = index > 0 ? String(index + 1) : '';

    const sameYear = new LocalDate(availability.start).localYear === new LocalDate(availability.end).localYear;
    const startFormat: Pattern =
      isNotBlank(availability.start) && isNotBlank(availability.end) && sameYear
        ? DateFormat.MONTH
        : DateFormat.LONG_MONTH_AND_YEAR;

    const start = new DateFormat(dateTranslations).format(availability.start, startFormat);
    const end = new DateFormat(dateTranslations).format(availability.end, DateFormat.LONG_MONTH_AND_YEAR);

    acc[`start${postFix}`] = start;
    acc[`end${postFix}`] = end;
    return acc;
  }, {});
};

export const createPreviousSearches = (
  { locale, locations, paxAmount, travelClass, travelDates, tripType, promoCode }: FlightSearchParams,
  previousSearches: PreviousSearch[]
): PreviousSearch[] => {
  const previousSearch: PreviousSearch = {
    paxAmount,
    travelClass,
    travelDates: {
      departureDate: travelDates.departureDate.id,
      returnDate: travelDates?.returnDate?.id,
    },
    tripType,
    origin: locations[0].origin,
    destination: locations[0].destination,
    creationDate: TzDate.now().millis,
    discountCode: promoCode || null,
    locale: locale,
  };

  const previousIndex = previousSearches.findIndex(
    (ps) =>
      ps.origin.locationCode === locations[0].origin.locationCode &&
      ps.destination.locationCode === locations[0].destination.locationCode
  );

  return previousIndex !== -1
    ? [previousSearch, ...previousSearches.slice(0, previousIndex), ...previousSearches.slice(previousIndex + 1)]
    : [previousSearch, ...previousSearches.slice(0, 1)];
};

export const getStartingFromPrice = (
  prices: DatePickerPrices,
  travelDates: GlobalBookingTripDates,
  isOneWay: boolean
): Amount => prices?.calendar?.[isOneWay ? travelDates?.departureDate?.id : travelDates?.returnDate?.id]?.amount;
